import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { I } from './Icons';

export interface MenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface MenuButtonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: MenuItem[];
  ariaLabel?: string;
}

const MENU_WIDTH = 160;

export function MenuButton({
  open,
  onOpenChange,
  items,
  ariaLabel = 'More actions',
}: MenuButtonProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [focusedIdx, setFocusedIdx] = useState<number>(0);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: Math.max(8, rect.right - MENU_WIDTH) });
    const firstEnabled = items.findIndex((it) => !it.disabled);
    setFocusedIdx(firstEnabled >= 0 ? firstEnabled : 0);
  }, [open, items]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
        triggerRef.current?.focus();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIdx((cur) => {
          const enabled = items
            .map((it, idx) => (it.disabled ? -1 : idx))
            .filter((x) => x >= 0);
          if (!enabled.length) return cur;
          const at = enabled.indexOf(cur);
          const step = e.key === 'ArrowDown' ? 1 : -1;
          const next = (at < 0 ? 0 : at + step + enabled.length) % enabled.length;
          return enabled[next];
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = items[focusedIdx];
        if (item && !item.disabled) {
          item.onClick();
          onOpenChange(false);
        }
      }
    };
    const handleMouseDown = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      onOpenChange(false);
    };
    const handleScroll = () => {
      onOpenChange(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, items, focusedIdx, onOpenChange]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          border: '1px solid transparent',
          background: 'transparent',
          color: 'var(--fg-3)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
        }}
      >
        <I.dots size={16} />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              minWidth: MENU_WIDTH,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-1)',
              borderRadius: 8,
              boxShadow: 'var(--shadow-md, 0 8px 24px rgba(46, 26, 72, 0.10))',
              padding: 4,
              zIndex: 1000,
              fontSize: 13,
            }}
          >
            {items.map((item, idx) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!item.disabled) {
                    item.onClick();
                    onOpenChange(false);
                  }
                }}
                onMouseEnter={() => {
                  if (!item.disabled) setFocusedIdx(idx);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderRadius: 4,
                  border: 'none',
                  background:
                    focusedIdx === idx && !item.disabled
                      ? 'var(--bg-surface-2)'
                      : 'transparent',
                  color: item.disabled ? 'var(--fg-4)' : 'var(--fg-1)',
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  font: 'inherit',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
